/**
 * Rich Text Editor Component
 * WYSIWYG editor with support for images, videos, YouTube embeds, and more
 * Includes HTML source code editing mode for complex HTML/SVG content
 */

import { useMemo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { adminApi, getImageUrl } from "services/api";
import { lightDark } from "utils/lightDark";

// Custom image blot to handle relative URLs
const ImageBlot = Quill.import("formats/image");
class CustomImage extends ImageBlot {
  static create(value) {
    const node = super.create(value);
    // Convert relative URLs to absolute URLs
    if (
      value &&
      !value.startsWith("http://") &&
      !value.startsWith("https://") &&
      !value.startsWith("data:")
    ) {
      value = getImageUrl(value);
    }
    node.setAttribute("src", value);
    return node;
  }

  static value(node) {
    return node.getAttribute("src");
  }
}
Quill.register(CustomImage, true);

// Helper function to detect if content contains complex HTML/SVG
const hasComplexHtml = (content) => {
  if (!content || typeof content !== "string") return false;

  // Check for SVG elements
  if (content.includes("<svg") || content.includes("</svg>")) return true;

  // Check for inline styles (style="...")
  if (content.match(/style\s*=\s*["'][^"']*["']/i)) return true;

  // Check for article, section, or div with classes
  if (content.match(/<(article|section|div)[^>]*class\s*=/i)) return true;

  // Check for code blocks with styling
  if (content.includes('class="code-block"') || content.includes('class="highlight-box"'))
    return true;

  return false;
};

function RichTextEditor({ value, onChange, label, error, helperText, height = 400 }) {
  const quillRef = useRef(null);
  const [isHtmlMode, setIsHtmlMode] = useState(() => {
    // Auto-detect if content has complex HTML/SVG and start in HTML mode
    return hasComplexHtml(value);
  });
  const [htmlContent, setHtmlContent] = useState(value || "");

  // Sync value changes from parent and detect complex HTML
  useEffect(() => {
    if (value !== htmlContent) {
      setHtmlContent(value || "");

      // If new value has complex HTML and we're in WYSIWYG mode, switch to HTML mode
      if (!isHtmlMode && hasComplexHtml(value)) {
        setIsHtmlMode(true);
      }
    }
  }, [value]);

  // Toggle between WYSIWYG and HTML mode
  const toggleHtmlMode = () => {
    if (!isHtmlMode) {
      // Switching to HTML mode - get current content
      setHtmlContent(value || "");
      setIsHtmlMode(true);
    } else {
      // Switching back to WYSIWYG mode - warn if complex HTML detected
      if (hasComplexHtml(htmlContent)) {
        const confirmed = window.confirm(
          "⚠️ Warning: This content contains complex HTML/SVG with inline styles.\n\n" +
            "Switching to Visual Editor will remove:\n" +
            "• SVG graphics\n" +
            '• Inline styles (style="...")\n' +
            "• Complex HTML structures\n\n" +
            "Are you sure you want to continue?\n\n" +
            "Recommendation: Keep using HTML Source mode for this content."
        );

        if (!confirmed) {
          return; // Stay in HTML mode
        }
      }

      // Update parent with HTML content and switch to WYSIWYG
      onChange(htmlContent);
      setIsHtmlMode(false);
    }
  };

  // Handle HTML content changes in source mode
  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    onChange(newHtml);
  };

  // Update existing images in the editor to use absolute URLs
  useEffect(() => {
    if (quillRef.current && value) {
      const quill = quillRef.current.getEditor();
      const delta = quill.getContents();
      let changed = false;

      delta.ops.forEach((op) => {
        if (op.insert && op.insert.image) {
          const src = op.insert.image;
          if (
            src &&
            !src.startsWith("http://") &&
            !src.startsWith("https://") &&
            !src.startsWith("data:")
          ) {
            op.insert.image = getImageUrl(src);
            changed = true;
          }
        }
      });

      if (changed) {
        quill.setContents(delta, "silent");
      }
    }
  }, [value]);

  // Image upload handler
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          // Upload image to server
          const formData = new FormData();
          formData.append("file", file);
          const response = await adminApi.uploadImage(formData);

          // Get the uploaded image URL from response.file.url
          const relativeUrl = response.file?.url || response.url;

          // Convert relative URL to absolute URL
          const imageUrl = getImageUrl(relativeUrl);

          // Insert image into editor
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageUrl);
          quill.setSelection(range.index + 1);
        } catch (error) {
          console.error("Image upload failed:", error);
          alert("Failed to upload image. Please try again.");
        }
      }
    };
  };

  // Video embed handler (YouTube, Vimeo, etc.)
  const videoHandler = () => {
    const url = prompt("Enter video URL (YouTube, Vimeo, etc.):");
    if (url) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);

      // Convert regular YouTube URL to embed URL
      let embedUrl = url;
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes("youtu.be/")) {
        const videoId = url.split("youtu.be/")[1]?.split("?")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes("vimeo.com/")) {
        const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }

      quill.insertEmbed(range.index, "video", embedUrl);
      quill.setSelection(range.index + 1);
    }
  };

  // Quill modules configuration
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
          video: videoHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  // Quill formats supported
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "video",
    "blockquote",
    "code-block",
  ];

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <MDBox display="flex" alignItems="center" gap={1}>
          {label && (
            <MDTypography variant="caption" fontWeight="regular" color="text">
              {label}
            </MDTypography>
          )}
          {isHtmlMode && (
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                backgroundColor: "#0066cc",
                color: "white",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "10px",
                textTransform: "uppercase",
              }}
            >
              HTML Mode
            </MDTypography>
          )}
        </MDBox>
        <MDButton
          size="small"
          variant="outlined"
          color={isHtmlMode ? "success" : "info"}
          onClick={toggleHtmlMode}
          startIcon={<Icon>{isHtmlMode ? "visibility" : "code"}</Icon>}
        >
          {isHtmlMode ? "Visual Editor" : "HTML Source"}
        </MDButton>
      </MDBox>

      {isHtmlMode ? (
        // HTML Source Code Mode
        <MDBox>
          <textarea
            value={htmlContent}
            onChange={handleHtmlChange}
            style={{
              width: "100%",
              minHeight: `min(${height}px, 50vh)`,
              maxHeight: "600px",
              fontFamily: "'Courier New', monospace",
              fontSize: "13px",
              padding: "15px",
              border: error ? "1px solid #f44335" : "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px",
              resize: "vertical",
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              lineHeight: "1.6",
              overflowY: "auto",
            }}
            placeholder="Paste or write HTML code here... Supports SVG, inline styles, and complex HTML structures."
          />
          <MDTypography
            variant="caption"
            color="info"
            sx={{ mt: 1, display: "block", fontSize: "0.75rem" }}
          >
            💡 <strong>HTML Mode Active:</strong> This mode preserves all HTML, SVG, inline styles,
            and complex structures. Your content will be saved exactly as written.
          </MDTypography>
        </MDBox>
      ) : (
        // WYSIWYG Mode
        <MDBox
          sx={{
            // Capped at half the viewport so a 400px editor still leaves a phone room for the form.
            "& .ql-container": {
              minHeight: `min(${height}px, 50vh)`,
              fontSize: "14px",
              fontFamily: "Roboto, sans-serif",
              border: error ? "1px solid #f44335" : "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "0 0 4px 4px",
              // In dark mode the writing area stays a light page, so content looks as it will be
              // published and any text colours the author picked stay readable.
              backgroundColor: lightDark("transparent", "#ffffff"),
              color: lightDark("inherit", "rgba(0, 0, 0, 0.87)"),
            },
            "& .ql-toolbar": {
              backgroundColor: "#f8f9fa",
              border: error ? "1px solid #f44335" : "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px 4px 0 0",
              borderBottom: "none",
              "@media (max-width: 767.95px)": {
                "& button": { width: "36px", height: "36px", padding: "6px" },
                "& .ql-picker": { height: "36px" },
              },
            },
            "& .ql-editor": {
              minHeight: `min(${height}px, 50vh)`,
              maxHeight: "600px",
              overflowY: "auto",
            },
            "& .ql-editor.ql-blank::before": {
              color: "rgba(0, 0, 0, 0.4)",
              fontStyle: "normal",
            },
            "& .ql-editor img": {
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "10px 0",
            },
            "& .ql-video": {
              width: "100%",
              height: "auto",
              aspectRatio: "16 / 9",
            },
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder="Write your content here... You can embed images, videos, and more!"
          />
        </MDBox>
      )}

      {helperText && !isHtmlMode && (
        <MDTypography
          variant="caption"
          color={error ? "error" : "text"}
          sx={{ mt: 0.5, display: "block", fontSize: "0.75rem" }}
        >
          {helperText}
        </MDTypography>
      )}
    </MDBox>
  );
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  height: PropTypes.number,
};

RichTextEditor.defaultProps = {
  value: "",
  label: "",
  error: false,
  helperText: "",
  height: 400,
};

export default RichTextEditor;
